import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {

  constructor() { }
  public exportAsExcelFile(json: any[], excelFileName: string): void {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    if (!json || json.length === 0) {
      return;
    }

    // Add header row
    const headers = Object.keys(json[0]);
    worksheet.addRow(headers);

    // Add data rows
    json.forEach(row => {
      worksheet.addRow(headers.map(h => row[h]));
    });

    // Set column widths and wrapText
    headers.forEach((header, index) => {
      worksheet.getColumn(index + 1).width = 20;
      worksheet.getColumn(index + 1).alignment = { wrapText: true };
    });

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { wrapText: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD2B48C' },
      };
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      this.saveAsExcelFile(buffer, excelFileName);
    });
  }

  exportStudyAsExcelFile(json: any[], excelFileName: string, studyDetails: any, studyStatus: string): void {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(studyDetails.studyName + ' Study Details');

    worksheet.getCell('A1').value = studyDetails.studyName + '_' + studyStatus;
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getColumn(1).width = 20;
    worksheet.getColumn(1).alignment = { wrapText: true };

    const headers = Object.keys(json[0]);
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(2, index + 1);
      cell.value = header;
      cell.font = { bold: true };
      cell.alignment = { wrapText: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD2B48C' },
      };
    });

    const startRow = 3;
    const startCol = 1;

    json.forEach((row, rowIndex) => {
      Object.values(row).forEach((value, colIndex) => {
        const cell = worksheet.getCell(rowIndex + startRow, colIndex + startCol);
        if(typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value instanceof Date) {
          cell.value = value as ExcelJS.CellValue;
        } else {
          cell.value = String(value);
        }
        cell.alignment = { wrapText: true };
      });
    });

    headers.forEach((header, index) => {
      worksheet.getColumn(index + startCol).width = 20;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      this.saveAsStudyExcelFile(buffer, studyDetails.studyName + ' ' + excelFileName);
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    this.downloadBlob(data, fileName + EXCEL_EXTENSION);
  }

  private saveAsStudyExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: 'application/octet-stream' });
    this.downloadBlob(data, fileName + '.xlsx');
  }

  exportScreeningLogAsExcelFile(json: any[], studyDetails: any, title: string, filename: string, customHeaders?: string[]): void {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(title);

    // Get number of columns dynamically from data or use provided headers
    const headers = customHeaders && customHeaders.length > 0 ? customHeaders : (json.length === 0 ? [] : Object.keys(json[0]));
    const totalColumns = headers.length;
    
    // Return early if no headers can be determined
    if (totalColumns === 0) {
      return;
    }
    
    const lastColumnLetter = this.getColumnLetter(totalColumns);

    // CALCULATE COLUMN WIDTHS FIRST (before setting row heights)
    const columnWidths: number[] = [];
    headers.forEach((header, colIndex) => {
      let maxLength = header.length;
      
      // Check all data rows for this column
      json.forEach(row => {
        const value = Object.values(row)[colIndex];
        const valueLength = String(value || '').length;
        if (valueLength > maxLength) {
          maxLength = valueLength;
        }
      });
      
      // Calculate width based on content
      let calculatedWidth = Math.max(15, Math.min(60, maxLength * 1.2));
      
      // Ensure reasonable minimum widths for better display
      if (colIndex === 0 || colIndex === 1) {
        // First two columns
        calculatedWidth = Math.max(calculatedWidth, 18);
      } else if (colIndex === 2) {
        // Third column
        calculatedWidth = Math.max(calculatedWidth, 15);
      } else {
        // Remaining columns
        calculatedWidth = Math.max(calculatedWidth, 18);
      }
      
      columnWidths.push(calculatedWidth);
      worksheet.getColumn(colIndex + 1).width = calculatedWidth;
    });

    // ROW 1: Title - merged across all columns, centered, bold, blue background
    worksheet.mergeCells(`A1:${lastColumnLetter}1`);
    const titleCell = worksheet.getCell('A1');
    titleCell.value = title;
    titleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4472C4' }, // Blue background
    };
    titleCell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    worksheet.getRow(1).height = 30;

    // HEADER SECTION: Fixed 4-column layout independent of data columns
    // Define fixed column widths for header section
    const headerCol1End = Math.min(2, totalColumns); // Columns A-B for "Internal Study Identifier"
    const headerCol2 = Math.min(3, totalColumns); // Column C for "Protocol"
    const headerCol3End = Math.min(Math.max(5, totalColumns - 2), totalColumns); // Columns D-E (or more) for "Study Title"
    const headerCol4Start = headerCol3End + 1; // Remaining columns for "PI"

    // ROWS 2 & 3 COMBINED: Header labels and values in merged vertical cells
    const studySummaryText = (studyDetails.studyName ? this.stripHtml(studyDetails.studyName) : '');
    const studyNameText = studyDetails.studyName || '';
    const protocolText = studyDetails.protocol || '';
    const piText = studyDetails.pi || '';
    const estimateWrappedLines = (text: string, widthChars: number): number => {
      if (!text) return 1;
      const lines = text.split('\n');
      return lines.reduce((sum, line) => {
        const safeWidth = Math.max(8, Math.floor(widthChars));
        return sum + Math.max(1, Math.ceil(line.length / safeWidth));
      }, 0);
    };

    // Internal Study Identifier combined cell
    if (headerCol1End >= 2) {
      worksheet.mergeCells(`A2:B3`);
    }
    worksheet.getCell('A2').value = `Internal Study Identifier (Study number/name):${studyNameText ? `\n${studyNameText}` : ''}`;
    worksheet.getCell('A2').font = { bold: true };
    worksheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
    worksheet.getCell('A2').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFB4C7E7' },
    };
    worksheet.getCell('A2').border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Protocol combined cell
    worksheet.mergeCells('C2:C3');
    worksheet.getCell('C2').value = `Protocol:${protocolText ? `\n${protocolText}` : ''}`;
    worksheet.getCell('C2').font = { bold: true };
    worksheet.getCell('C2').alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
    worksheet.getCell('C2').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFB4C7E7' },
    };
    worksheet.getCell('C2').border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Study Title combined cell
    const studyTitleEnd = Math.min(headerCol3End, totalColumns);
    if (studyTitleEnd > 3) {
      worksheet.mergeCells(`D2:${this.getColumnLetter(studyTitleEnd)}3`);
    }
    worksheet.getCell('D2').value = `Study Title:${studySummaryText ? `\n${studySummaryText}` : ''}`;
    worksheet.getCell('D2').font = { bold: true };
    worksheet.getCell('D2').alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
    worksheet.getCell('D2').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFB4C7E7' },
    };
    worksheet.getCell('D2').border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // PI combined cell
    if (headerCol4Start <= totalColumns) {
      worksheet.mergeCells(`${this.getColumnLetter(headerCol4Start)}2:${lastColumnLetter}3`);
      worksheet.getCell(this.getColumnLetter(headerCol4Start) + '2').value = `PI:${piText ? `\n${piText}` : ''}`;
      worksheet.getCell(this.getColumnLetter(headerCol4Start) + '2').font = { bold: true };
      worksheet.getCell(this.getColumnLetter(headerCol4Start) + '2').alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
      worksheet.getCell(this.getColumnLetter(headerCol4Start) + '2').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFB4C7E7' },
      };
      worksheet.getCell(this.getColumnLetter(headerCol4Start) + '2').border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    }

    // Set heights for combined rows based on estimated wrapped content
    const section1Width = (worksheet.getColumn(1).width || 15) + (worksheet.getColumn(2).width || 15);
    const section2Width = worksheet.getColumn(3).width || 15;
    let section3Width = 0;
    for (let col = 4; col <= studyTitleEnd; col++) {
      section3Width += worksheet.getColumn(col).width || 15;
    }
    let section4Width = 0;
    if (headerCol4Start <= totalColumns) {
      for (let col = headerCol4Start; col <= totalColumns; col++) {
        section4Width += worksheet.getColumn(col).width || 15;
      }
    }

    const row2Texts = [
      `Internal Study Identifier (Study number/name):${studyNameText ? `\n${studyNameText}` : ''}`,
      `Protocol:${protocolText ? `\n${protocolText}` : ''}`,
      `Study Title:${studySummaryText ? `\n${studySummaryText}` : ''}`,
      `PI:${piText ? `\n${piText}` : ''}`
    ];

    const estimatedLines = [
      estimateWrappedLines(row2Texts[0], section1Width),
      estimateWrappedLines(row2Texts[1], section2Width),
      estimateWrappedLines(row2Texts[2], section3Width || 15),
      estimateWrappedLines(row2Texts[3], section4Width || 15)
    ];

    const maxLines = Math.max(...estimatedLines);
    worksheet.getRow(2).height = Math.max(45, Math.min(180, maxLines * 20 + 10));
    worksheet.getRow(3).height = 0.1;

    // ROW 4: Empty row for spacing
    worksheet.getRow(4).height = 5;

    // ROW 5: Data table headers
    let maxHeaderLength = 0;
    headers.forEach((header, index) => {
      if (header.length > maxHeaderLength) {
        maxHeaderLength = header.length;
      }
      const cell = worksheet.getCell(5, index + 1);
      cell.value = header;
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD2B48C' }, // Tan background
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
    // Set header row height
    worksheet.getRow(5).height = Math.max(30, Math.min(80, Math.ceil(maxHeaderLength * 1.5)));

    // ROW 6+: Data rows
    const startRow = 6;
    json.forEach((row, rowIndex) => {
      let maxContentLength = 0;
      Object.values(row).forEach((value, colIndex) => {
        const cell = worksheet.getCell(rowIndex + startRow, colIndex + 1);
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value instanceof Date) {
          cell.value = value as ExcelJS.CellValue;
        } else {
          cell.value = String(value);
        }
        cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        // Track max content length for height calculation
        const contentLength = String(value || '').length;
        if (contentLength > maxContentLength) {
          maxContentLength = contentLength;
        }
      });
      // Calculate row height based on content (minimum 20, maximum 120)
      worksheet.getRow(rowIndex + startRow).height = Math.max(20, Math.min(120, Math.ceil(maxContentLength * 1.2)));
    });

    // Add date row at the end
    const dateRow = startRow + json.length + 1; // +2 for spacing
    const currentDate = new Date();
    const day = currentDate.getDate().toString().padStart(2, '0');
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    const formattedDate = `${day}${month}${year}`;

    worksheet.mergeCells(`A${dateRow}:${lastColumnLetter}${dateRow}`);
    const dateCell = worksheet.getCell(`A${dateRow}`);
    dateCell.value = formattedDate;
    dateCell.font = { bold: true, size: 12 };
    dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(dateRow).height = 25;

    // Save the file
    workbook.xlsx.writeBuffer().then((buffer) => {
      const data: Blob = new Blob([buffer], { type: 'application/octet-stream' });
      const fileName = `${studyDetails.studyName + filename}.xlsx`;
      this.downloadBlob(data, fileName);
    });
  }

  private downloadBlob(data: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  private getColumnLetter(columnNumber: number): string {
    let columnLetter = '';
    while (columnNumber > 0) {
      const remainder = (columnNumber - 1) % 26;
      columnLetter = String.fromCharCode(65 + remainder) + columnLetter;
      columnNumber = Math.floor((columnNumber - 1) / 26);
    }
    return columnLetter;
  }

  // public exportAsExcelFileWithHighlight(json: any[], excelFileName: string, highlightCells?: { row: number, col: number }[]): void {
  //   const workbook = new ExcelJS.Workbook();
  //   const worksheet = workbook.addWorksheet('Sheet1');

  //   if (!json || json.length === 0) {
  //     return;
  //   }

  //   // Add header row
  //   const headers = Object.keys(json[0]);
  //   worksheet.addRow(headers);

  //   // Add data rows
  //   json.forEach(row => {
  //     worksheet.addRow(headers.map(h => row[h]));
  //   });

  //   // Set column widths and wrapText
  //   headers.forEach((header, index) => {
  //     worksheet.getColumn(index + 1).width = 20;
  //     worksheet.getColumn(index + 1).alignment = { wrapText: true };
  //   });

  //   // Style header row
  //   const headerRow = worksheet.getRow(1);
  //   headerRow.font = { bold: true };
  //   headerRow.alignment = { wrapText: true };
  //   headerRow.eachCell((cell) => {
  //     cell.fill = {
  //       type: 'pattern',
  //       pattern: 'solid',
  //       fgColor: { argb: 'FFD2B48C' },
  //     };
  //   });

  //   // Highlight specified cells in yellow
  //   if (highlightCells && highlightCells.length > 0) {
  //     highlightCells.forEach(cellPos => {
  //       const cell = worksheet.getCell(cellPos.row + 1, cellPos.col + 1); // +1 because Excel is 1-indexed and we need to account for header
  //       cell.fill = {
  //         type: 'pattern',
  //         pattern: 'solid',
  //         fgColor: { argb: 'FFFFFF00' }, // Yellow color
  //       };
  //     });
  //   }

  //   workbook.xlsx.writeBuffer().then((buffer) => {
  //     this.saveAsExcelFile(buffer, excelFileName);
  //   });
  // }
}
